import { type CSSProperties, type PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RichInlineMd } from '../components/RichInlineMd';
import type { ExplicitProfile } from '../utils/whyPersonalization';
import { moduleScoreForProfile } from '../utils/whyPersonalization';

const bigIdeasFoundations = new URL('../../../../../images/Big Ideas/Foundations.jpg', import.meta.url).toString();
const bigIdeasQuran = new URL('../../../../../images/Big Ideas/Quran.jpg', import.meta.url).toString();
const bigIdeasProphet = new URL('../../../../../images/Big Ideas/Prophet.jpg', import.meta.url).toString();

export type ExploreModule = {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  topic_tags_json: string;
  sensitivity: number;
  topic_family: string | null;
  site_section: string | null;
  estimated_read_minutes: number;
  topic_slugs_json: string;
};

export type ExploreTopic = {
  id: string;
  slug: string;
  title: string;
  family: string | null;
  summary: string | null;
  module_count?: number;
};

export type ExploreRes = {
  topics: ExploreTopic[];
  modules: ExploreModule[];
};

export type ClusterKey =
  | 'foundations'
  | 'quran'
  | 'prophet'
  | 'theology'
  | 'ethics_society'
  | 'comparative'
  | 'difficult';

/** Full-bleed scene art per cluster (existing visual treatment; catalog is now topic-driven). */
export const CLUSTER_SCENE_BACKGROUNDS: Partial<Record<ClusterKey, string>> = {
  foundations: bigIdeasFoundations,
  quran: bigIdeasQuran,
  prophet: bigIdeasProphet,
};

export const CHIP_ORDER: ClusterKey[] = [
  'foundations',
  'quran',
  'prophet',
  'theology',
  'ethics_society',
  'difficult',
  'comparative',
];

export const CLUSTER_COPY: Record<ClusterKey, { title: string; blurb: string }> = {
  foundations: {
    title: 'Foundations',
    blurb: 'Meaning, God, revelation, and worship-the conceptual floor for everything else.',
  },
  quran: {
    title: 'Qur\'an',
    blurb: 'Revelation, preservation, language, and how Muslims read scripture with care.',
  },
  prophet: {
    title: 'Prophet',
    blurb: 'Prophethood, history, and the role of Muhammad (peace be upon him) in Muslim life.',
  },
  theology: {
    title: 'Theology',
    blurb: 'Names, attributes, monotheism, and classical questions of belief.',
  },
  ethics_society: {
    title: 'Ethics & society',
    blurb: 'Law, mercy, community life, and how moral reasoning unfolds in practice.',
  },
  difficult: {
    title: 'Difficult questions',
    blurb: 'Serious topics-apostasy, jihad, gender, law-addressed with calm sourcing and intellectual honesty, not sensational framing.',
  },
  comparative: {
    title: 'Comparative religion',
    blurb: 'Side-by-side entry points-neutral in tone, precise in terms. Comparison is for understanding, not scoring points.',
  },
};

function legacyScoreKey(topic: ExploreTopic, mods: ExploreModule[]): ClusterKey {
  const slug = topic.slug.toLowerCase();
  const fam = (topic.family || '').toLowerCase();
  const title = topic.title.toLowerCase();
  const tags = mods.flatMap((m) => parseTags(m.topic_tags_json).map((t) => t.toLowerCase()));

  const hasTag = (needle: string) => tags.some((tag) => tag === needle || tag.includes(needle));

  if (slug.includes('christianity') || slug.includes('atheism') || fam === 'comparative') return 'comparative';
  if (
    slug.includes('jihad') ||
    slug.includes('war') ||
    slug.includes('power') ||
    slug.includes('family') ||
    slug.includes('women') ||
    slug.includes('relationships')
  ) {
    return 'difficult';
  }
  if (slug === 'the-quran' || fam === 'quran' || hasTag('quran') || hasTag('scripture')) return 'quran';
  if (slug === 'prophethood' || slug.includes('prophethood') || title.includes('prophethood')) return 'prophet';
  if (slug === 'god-and-oneness' || hasTag('tawhid') || fam === 'foundations') return 'foundations';
  if (fam === 'ethics') return 'ethics_society';
  return 'theology';
}

function topicSceneBackground(topic: ExploreTopic, mods: ExploreModule[]): string | undefined {
  const scoreKey = legacyScoreKey(topic, mods);
  return CLUSTER_SCENE_BACKGROUNDS[scoreKey];
}

function topicKicker(topic: ExploreTopic, mods: ExploreModule[]): string {
  const fam = (topic.family || '').toLowerCase();
  if (fam === 'quran') return 'Revelation & text';
  if (fam === 'comparative') return 'Faiths in context';
  if (fam === 'spirituality') return 'Inner life & return';
  if (fam === 'history') return 'History & legacy';
  if (fam === 'pastoral') return 'Steadying inquiry';
  if (fam === 'ethics') return 'Ethics & lived community';
  if (topic.slug === 'prophethood') return 'Messengers & history';
  if (topic.slug === 'god-and-oneness') return 'Core orientation';
  if (mods.some((m) => m.sensitivity >= 0.58)) return 'Honest engagement';
  return 'Belief & meaning';
}

export function parseTags(json: string): string[] {
  try {
    const t = JSON.parse(json || '[]');
    return Array.isArray(t) ? t.map(String) : [];
  } catch {
    return [];
  }
}

export function parseTopicSlugs(json: string): string[] {
  try {
    const t = JSON.parse(json || '[]');
    return Array.isArray(t) ? t.map(String) : [];
  } catch {
    return [];
  }
}

export function moduleHasTopic(m: ExploreModule, topicSlug: string): boolean {
  return parseTopicSlugs(m.topic_slugs_json).includes(topicSlug);
}

export function matchesSearch(m: ExploreModule, q: string): boolean {
  if (!q.trim()) return true;
  const needle = q.toLowerCase().trim();
  const tags = parseTags(m.topic_tags_json).join(' ').toLowerCase();
  const topics = parseTopicSlugs(m.topic_slugs_json).join(' ').toLowerCase();
  const hay = `${m.title} ${m.summary ?? ''} ${m.topic_family ?? ''} ${m.site_section ?? ''} ${tags} ${topics}`.toLowerCase();
  return hay.includes(needle);
}

export function chipLabel(topic: ExploreTopic): string {
  return topic.title;
}

export function primaryTopicForModule(m: ExploreModule, topics: ExploreTopic[]): ExploreTopic | null {
  const bySlug = new Map(topics.map((t) => [t.slug, t]));
  for (const slug of parseTopicSlugs(m.topic_slugs_json)) {
    const topic = bySlug.get(slug);
    if (topic) return topic;
  }
  return null;
}

function ModuleTags({ m }: { m: ExploreModule }) {
  const tags = parseTags(m.topic_tags_json).slice(0, 4);
  if (!tags.length) return null;
  return (
    <div className="wi-explore-tags" aria-hidden>
      {tags.map((t) => (
        <span key={t} className="wi-explore-tag">
          {t}
        </span>
      ))}
    </div>
  );
}

function ModuleCard({
  m,
  featured,
}: {
  m: ExploreModule;
  featured?: boolean;
}) {
  return (
    <article className={`wi-explore-card${featured ? ' wi-explore-card--featured' : ''}`}>
      <Link to={`/m/${m.slug}`} className="wi-explore-card__link">
        <h3 className="wi-explore-card__title">{m.title}</h3>
      </Link>
      {featured && m.summary ? <RichInlineMd text={m.summary} className="wi-explore-card__excerpt" /> : null}
      {!featured && m.summary ? <RichInlineMd text={m.summary} className="wi-explore-card__summary" /> : null}
      <div className="wi-explore-card__meta">
        <ModuleTags m={m} />
        <span className="wi-explore-read">{m.estimated_read_minutes ?? 12} min read</span>
      </div>
    </article>
  );
}

function ModuleCardSide({
  m,
  onClick,
  className,
  ariaLabel,
}: {
  m: ExploreModule;
  onClick: () => void;
  className?: string;
  ariaLabel: string;
}) {
  return (
    <button type="button" className={`wi-explore-cluster-carousel__side-card ${className ?? ''}`} onClick={onClick} aria-label={ariaLabel}>
      <h3 className="wi-explore-cluster-carousel__side-card-title">{m.title}</h3>
      {m.summary ? <RichInlineMd text={m.summary} className="wi-explore-cluster-carousel__side-card-summary" /> : null}
      <div className="wi-explore-cluster-carousel__side-card-meta">
        <ModuleTags m={m} />
        <span className="wi-explore-read">{m.estimated_read_minutes ?? 12} min read</span>
      </div>
    </button>
  );
}

export function ExploreClusterCarousel({
  topic,
  sectionIndex,
  mods,
  explicitProfile,
  adaptEnabled,
}: {
  topic: ExploreTopic;
  sectionIndex: number;
  mods: ExploreModule[];
  explicitProfile: ExplicitProfile | null;
  adaptEnabled: boolean;
}) {
  const scoreKey = legacyScoreKey(topic, mods);
  const kicker = topicKicker(topic, mods);
  const initialActive = useMemo(() => {
    if (!mods.length) return 0;
    if (adaptEnabled && explicitProfile) {
      let best = 0;
      let score = -Infinity;
      for (let i = 0; i < mods.length; i += 1) {
        const m = mods[i];
        const s = moduleScoreForProfile(m, scoreKey, explicitProfile);
        if (s > score) {
          score = s;
          best = i;
        }
      }
      return best;
    }

    let best = 0;
    let score = -1;
    for (let i = 0; i < mods.length; i += 1) {
      const m = mods[i];
      const s = (m.summary?.length ?? 0) + (m.estimated_read_minutes ?? 0) * 0.8;
      if (s > score) {
        score = s;
        best = i;
      }
    }
    return best;
  }, [mods, adaptEnabled, explicitProfile, scoreKey]);
  const [active, setActive] = useState(0);
  const regionRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);

  const modsSig = mods.map((m) => m.slug).join('|');

  useEffect(() => {
    setActive(initialActive);
  }, [topic.slug, modsSig, initialActive]);

  const n = mods.length;
  const goPrev = useCallback(() => {
    setActive((a) => (n <= 1 ? a : (a - 1 + n) % n));
  }, [n]);
  const goNext = useCallback(() => {
    setActive((a) => (n <= 1 ? a : (a + 1) % n));
  }, [n]);

  useEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goNext();
      }
    };
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold: 0.34, rootMargin: '-8% 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const prevIdx = n > 1 ? (active - 1 + n) % n : -1;
  const nextIdx = n > 1 ? (active + 1) % n : -1;
  const center = mods[active];
  const leftMod = prevIdx >= 0 ? mods[prevIdx] : null;
  const rightMod = nextIdx >= 0 ? mods[nextIdx] : null;

  const labelId = `wi-explore-h-${topic.slug}`;
  const sceneBg = topicSceneBackground(topic, mods);
  const sceneAlign = (sectionIndex - 1) % 2 === 0 ? 'left' : 'right';

  const handlePointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!sceneBg) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      e.currentTarget.style.setProperty('--wi-bigideas-cursor-x', `${x.toFixed(2)}%`);
      e.currentTarget.style.setProperty('--wi-bigideas-cursor-y', `${y.toFixed(2)}%`);
    },
    [sceneBg]
  );

  const clusterInner = (
    <div className="wi-explore-inner">
      <div className="wi-explore-cluster__scene">
        <header
          className={`wi-explore-cluster__head wi-explore-cluster__head--premium${sceneBg ? ' wi-explore-cluster__head--on-scene' : ''}`}
        >
          <p className="wi-explore-cluster__kicker">
            <span className="wi-explore-cluster__index" aria-hidden>
              {String(sectionIndex).padStart(2, '0')}
            </span>
            <span className="wi-explore-cluster__kicker-text">{kicker}</span>
          </p>
          <h2 id={labelId} className="wi-explore-cluster__title">
            {topic.title}
          </h2>
          <p className="wi-explore-cluster__blurb">{topic.summary ?? ''}</p>
        </header>

        <div
          ref={regionRef}
          className="wi-explore-cluster-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label={`${topic.title} modules`}
          tabIndex={0}
        >
          <div className="wi-explore-cluster-carousel__stage" aria-live="polite">
            <div className="wi-explore-cluster-carousel__side-wrap">
              {leftMod ? (
                <ModuleCardSide
                  m={leftMod}
                  className="wi-explore-cluster-carousel__side wi-explore-cluster-carousel__side--left"
                  onClick={goPrev}
                  ariaLabel={`Show previous: ${leftMod.title}`}
                />
              ) : (
                <div className="wi-explore-cluster-carousel__side wi-explore-cluster-carousel__side--ghost" aria-hidden />
              )}
            </div>

            <div className="wi-explore-cluster-carousel__center">
              <ModuleCard m={center} featured />
            </div>

            <div className="wi-explore-cluster-carousel__side-wrap">
              {rightMod ? (
                <ModuleCardSide
                  m={rightMod}
                  className="wi-explore-cluster-carousel__side wi-explore-cluster-carousel__side--right"
                  onClick={goNext}
                  ariaLabel={`Show next: ${rightMod.title}`}
                />
              ) : (
                <div className="wi-explore-cluster-carousel__side wi-explore-cluster-carousel__side--ghost" aria-hidden />
              )}
            </div>
          </div>

          <div className="wi-explore-cluster-carousel__nav" aria-hidden={n <= 1}>
            <button type="button" className="wi-explore-cluster-carousel__nav-btn" onClick={goPrev} disabled={n <= 1}>
              Previous
            </button>
            <p className="wi-explore-cluster-carousel__nav-meta">
              <span>{active + 1}</span> of <span>{n}</span>
            </p>
            <button type="button" className="wi-explore-cluster-carousel__nav-btn" onClick={goNext} disabled={n <= 1}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      id={`wi-explore-cluster-${topic.slug}`}
      className={`wi-explore-cluster wi-explore-cluster--${scoreKey} wi-explore-cluster--carousel${inView ? ' is-in-view' : ''}${sceneBg ? ` wi-explore-cluster--has-scene wi-explore-cluster--scene-${sceneAlign}` : ''}`}
      aria-labelledby={labelId}
    >
      {sceneBg ? (
        <div
          className="wi-explore-cluster-scene"
          onPointerMove={handlePointerMove}
          style={
            {
              '--wi-bigideas-cursor-x': '50%',
              '--wi-bigideas-cursor-y': '42%',
            } as CSSProperties
          }
        >
          <img
            className="wi-explore-cluster-scene__bg"
            src={sceneBg}
            alt=""
            loading={sectionIndex <= 2 ? 'eager' : 'lazy'}
            decoding="async"
          />
          <div className="wi-explore-cluster-scene__veil" aria-hidden />
          <div className="wi-explore-cluster-scene__cursor" aria-hidden />
          <div className="wi-explore-cluster-scene__wrap">{clusterInner}</div>
        </div>
      ) : (
        clusterInner
      )}
    </section>
  );
}
